/**
 * TPRA Vendors API Request Functions & React Query Hooks
 *
 * Handles HTTP requests to the TPRA Dashboard vendors endpoint
 * with proper error handling, transformation, and type safety.
 * Includes TanStack Query hooks for React components.
 */

import { get } from '@/infra/rest';
import { APIEndpoints } from '@/shared/constants/Endpoints';
import { tpraVendorsQueryKeys } from '@/shared/constants/QueryKeys';
import { addNotification } from '@/shared/states/notification';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  TPRAVendorsQueryParams,
  TPRAVendorsResponse,
} from '../types/vendors';
import { transformVendorsData } from '../utils/transformVendorsData';

/**
 * Fetches TPRA vendors list from the API
 *
 * @param params - Query parameters for filtering, pagination, sorting
 * @param memberIdNameMapping - Optional mapping of member IDs to names for resolving created_by
 * @returns Transformed vendor data with UI-compatible fields
 * @throws Error if API request fails
 *
 * @example
 * ```typescript
 * const vendors = await getTPRAVendors({
 *   page: 1,
 *   page_size: 50,
 *   risk: 'High',
 *   search: 'asana'
 * }, memberMapping);
 * ```
 */
export async function getTPRAVendors(
  params: TPRAVendorsQueryParams,
  memberIdNameMapping?: Record<string, string>
): Promise<TPRAVendorsResponse> {
  try {
    // Make API request
    const response = await get<TPRAVendorsResponse>({
      url: APIEndpoints.TPRADashboardVendors,
      config: {
        params: sanitizeQueryParams(params),
      },
    });

    // Validate response structure
    validateAPIResponse(response);

    // Log data quality issues for debugging
    const emptyVendorNames = response.vendors.filter(
      (v) => !v.vendor_name || !v.vendor_name.trim()
    );
    const emptyRiskRatings = response.vendors.filter(
      (v) => !v.risk_rating || !v.risk_rating.trim()
    );
    const nullLastAssessed = response.vendors.filter((v) => !v.last_assessed_at);

    if (emptyVendorNames.length > 0) {
      console.warn(
        `[Data Quality] ${emptyVendorNames.length} vendors with empty/whitespace names`
      );
    }
    if (emptyRiskRatings.length > 0) {
      console.warn(
        `[Data Quality] ${emptyRiskRatings.length} vendors with empty risk ratings`
      );
    }
    if (nullLastAssessed.length > 0) {
      console.info(
        `[Data Quality] ${nullLastAssessed.length} vendors never assessed`
      );
    }

    // Transform API data to UI format with member mapping
    const transformed = transformVendorsData(response, memberIdNameMapping);

    return transformed;
  } catch (error) {
    // Log error for debugging
    console.error('Error fetching TPRA vendors:', error);

    // Add user notification
    addNotification({
      type: 'error',
      title: 'Failed to load vendors',
      message:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching vendors.',
    });

    // Re-throw for React Query error boundaries
    throw error;
  }
}

/**
 * Sanitizes query parameters by removing undefined/null values
 * Ensures clean API requests without empty parameters
 *
 * @param params - Raw query parameters
 * @returns Sanitized parameters object
 */
function sanitizeQueryParams(
  params: TPRAVendorsQueryParams
): Record<string, string | number> {
  const sanitized: Record<string, string | number> = {};

  // Only include defined parameters
  if (params.page !== undefined) {
    sanitized.page = params.page;
  }

  if (params.page_size !== undefined) {
    sanitized.page_size = params.page_size;
  }

  if (params.search && params.search.trim()) {
    sanitized.search = params.search.trim();
  }

  if (params.risk) {
    sanitized.risk = params.risk;
  }

  if (params.status) {
    sanitized.status = params.status;
  }

  if (params.sort_by) {
    sanitized.sort_by = params.sort_by;
  }

  if (params.sort_order) {
    sanitized.sort_order = params.sort_order;
  }

  return sanitized;
}

/**
 * Validates API response structure
 * Ensures response has required fields and correct types
 *
 * Note: Lenient validation to handle real-world data quality issues:
 * - Empty strings allowed for risk_rating, vendor_name
 * - Null values allowed for last_assessed_at, vendor_logo_url
 * - created_by is string (user ID), not object
 *
 * @param response - API response to validate
 * @throws Error if response is invalid
 */
function validateAPIResponse(response: TPRAVendorsResponse): void {
  // Check required fields exist
  if (typeof response.success !== 'boolean') {
    throw new Error('Invalid API response: missing or invalid "success" field');
  }

  if (typeof response.total_vendors !== 'number') {
    throw new Error(
      'Invalid API response: missing or invalid "total_vendors" field'
    );
  }

  if (!Array.isArray(response.vendors)) {
    throw new Error('Invalid API response: "vendors" must be an array');
  }

  // Validate pagination metadata
  if (typeof response.page !== 'number' || response.page < 1) {
    throw new Error('Invalid API response: invalid "page" value');
  }

  if (typeof response.page_size !== 'number' || response.page_size < 1) {
    throw new Error('Invalid API response: invalid "page_size" value');
  }

  if (typeof response.total_pages !== 'number' || response.total_pages < 0) {
    throw new Error('Invalid API response: invalid "total_pages" value');
  }

  // Validate each vendor record (lenient validation)
  response.vendors.forEach((vendor, index) => {
    // Critical fields that must exist
    if (!vendor.id) {
      throw new Error(
        `Invalid vendor at index ${index}: missing required field "id"`
      );
    }

    // vendor_name can be empty/whitespace (will be handled in transformation)
    if (vendor.vendor_name === undefined) {
      throw new Error(
        `Invalid vendor at index ${index}: missing "vendor_name" field`
      );
    }

    // risk_rating can be empty string (will be mapped to 'Unknown')
    if (vendor.risk_rating === undefined) {
      throw new Error(
        `Invalid vendor at index ${index}: missing "risk_rating" field`
      );
    }

    // last_assessed_at can be null (will show 'Never')
    if (vendor.last_assessed_at === undefined) {
      throw new Error(
        `Invalid vendor at index ${index}: missing "last_assessed_at" field`
      );
    }

    // assessment_status must be valid enum value
    if (!vendor.assessment_status) {
      throw new Error(
        `Invalid vendor at index ${index}: missing required field "assessment_status"`
      );
    }

    const validStatuses = ['intake_form', 'due_diligence', 'review', 'reassessment', 'complete'];
    if (!validStatuses.includes(vendor.assessment_status)) {
      throw new Error(
        `Invalid vendor at index ${index}: invalid "assessment_status" value "${vendor.assessment_status}"`
      );
    }

    // total_issues must be a non-negative number
    if (
      typeof vendor.total_issues !== 'number' ||
      vendor.total_issues < 0
    ) {
      throw new Error(
        `Invalid vendor at index ${index}: invalid "total_issues" value`
      );
    }

    // issues_by_severity must exist
    if (!vendor.issues_by_severity) {
      throw new Error(
        `Invalid vendor at index ${index}: missing "issues_by_severity"`
      );
    }

    // created_by must exist (string user ID)
    if (!vendor.created_by || typeof vendor.created_by !== 'string') {
      throw new Error(
        `Invalid vendor at index ${index}: missing or invalid "created_by" (expected user ID string)`
      );
    }
  });
}

/**
 * Pre-flight check for API parameters
 * Validates parameters before making API call
 *
 * @param params - Query parameters to validate
 * @throws Error if parameters are invalid
 */
export function validateQueryParams(params: TPRAVendorsQueryParams): void {
  if (params.page !== undefined && (params.page < 1 || !Number.isInteger(params.page))) {
    throw new Error('Invalid query parameter: "page" must be a positive integer');
  }

  if (
    params.page_size !== undefined &&
    (params.page_size < 1 || params.page_size > 100 || !Number.isInteger(params.page_size))
  ) {
    throw new Error(
      'Invalid query parameter: "page_size" must be between 1 and 100'
    );
  }

  if (params.risk && !['High', 'Medium', 'Low'].includes(params.risk)) {
    throw new Error(
      'Invalid query parameter: "risk" must be "High", "Medium", or "Low"'
    );
  }

  if (
    params.status &&
    ![
      'intake_form',
      'due_diligence',
      'review',
      'reassessment',
      'complete',
    ].includes(params.status)
  ) {
    throw new Error(
      'Invalid query parameter: "status" must be a valid API status value'
    );
  }

  if (
    params.sort_by &&
    ![
      'vendor_name',
      'last_assessed_at',
      'risk_rating',
      'total_issues',
    ].includes(params.sort_by)
  ) {
    throw new Error(
      'Invalid query parameter: "sort_by" must be a valid sortable field'
    );
  }

  if (params.sort_order && !['asc', 'desc'].includes(params.sort_order)) {
    throw new Error(
      'Invalid query parameter: "sort_order" must be "asc" or "desc"'
    );
  }
}

/**
 * React Query hook for fetching TPRA vendors
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 * - Placeholder data during refetch
 *
 * @param params - Query parameters for filtering, pagination, sorting
 * @param memberIdNameMapping - Optional mapping of member IDs to names
 * @returns React Query result with vendor data
 *
 * @example
 * ```typescript
 * function VendorsTable() {
 *   const { data, isLoading, isError, error, refetch } = useGetTPRAVendors({
 *     page: 1,
 *     page_size: 50,
 *     risk: 'High'
 *   }, memberMapping);
 *
 *   if (isLoading) return <LoadingSpinner />;
 *   if (isError) return <ErrorMessage error={error} />;
 *
 *   return <Table data={data.vendors} />;
 * }
 * ```
 */
export function useGetTPRAVendors(
  params: TPRAVendorsQueryParams,
  memberIdNameMapping?: Record<string, string>
): UseQueryResult<TPRAVendorsResponse, Error> {
  return useQuery({
    // Query key includes params for proper cache invalidation
    queryKey: tpraVendorsQueryKeys.list(params),

    // Query function
    queryFn: () => {
      // Validate params before making request
      validateQueryParams(params);
      return getTPRAVendors(params, memberIdNameMapping);
    },

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes - cache retention time

    // Retry Strategy (exponential backoff)
    retry: 2, // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Max 5s delay

    // Refetch Behavior
    refetchOnWindowFocus: true, // Refetch when returning to browser tab
    refetchOnMount: true, // Always fresh data on component mount
    refetchOnReconnect: true, // Refetch when network reconnects

    // UX Optimization
    placeholderData: (previousData) => previousData, // Keep old data while refetching

    // Error Handling
    // Errors automatically surface through isError/error properties
    // User notifications handled in getTPRAVendors function
  });
}

/**
 * Hook variant with enabled/disabled control
 * Useful for conditional fetching
 *
 * @param params - Query parameters
 * @param memberIdNameMapping - Optional mapping of member IDs to names
 * @param enabled - Whether to execute the query
 * @returns React Query result
 *
 * @example
 * ```typescript
 * // Only fetch when tab is active
 * const { data } = useGetTPRAVendorsConditional(params, memberMapping, isActiveTab);
 * ```
 */
export function useGetTPRAVendorsConditional(
  params: TPRAVendorsQueryParams,
  memberIdNameMapping: Record<string, string> | undefined,
  enabled: boolean
): UseQueryResult<TPRAVendorsResponse, Error> {
  return useQuery({
    queryKey: tpraVendorsQueryKeys.list(params),
    queryFn: () => {
      validateQueryParams(params);
      return getTPRAVendors(params, memberIdNameMapping);
    },
    enabled, // Only run query when enabled=true
    staleTime: 0,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });
}
